import axios from "axios";
import { getCookie, setCookie } from "cookies-next";

const TOKEN_KEY = "isnet_auth_token";

const apiClient = axios.create({
  baseURL: process.env.ISNETURL,
  timeout: 10000,
});

// Get token from cookie or request new one if needed
export const getAccessToken = async () => {
  const token = getCookie(TOKEN_KEY);

  if (!token) {
    try {
      const response = await axios.post(
        `${process.env.FIXITYURL}/Auth/Auth/CreateToken`,
        {
          email: process.env.email,
          password: process.env.password,
        }
      );
      const newToken = response.data.data.accessToken;

      setCookie(TOKEN_KEY, newToken, {
        maxAge: 60 * 60 * 24, // 24 hours
        path: "/",
        secure: process.env.NODE_ENV === "production",
        sameSite: "lax",
      });

      return newToken;
    } catch (error) {
      console.error("Failed to get new token:", error);
      return null;
    }
  }

  return token;
};

// Request interceptor
apiClient.interceptors.request.use(async (config) => {
  const token = await getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Clear existing token
      setCookie(TOKEN_KEY, "", { maxAge: 0 });

      // Get new token
      const newToken = await getAccessToken();

      if (newToken) {
        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      }
    }

    return Promise.reject(error);
  }
);

// Server-side API client
export const createServerApiClient = async () => {
  try {
    // In server-side, we always get a fresh token
    const response = await axios.post(
      `${process.env.FIXITYURL}/Auth/Auth/CreateToken`,
      {
        email: process.env.email,
        password: process.env.password,
      }
    );
    const token = response.data.data.accessToken;

    return axios.create({
      baseURL: process.env.ISNETURL,
      timeout: 10000,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch (error) {
    return axios.create({
      baseURL: process.env.ISNETURL,
      timeout: 10000,
    });
  }
};

export default apiClient;
